import { useEffect, useState } from "react";
import { NavLink, Routes, Route, useNavigate } from "react-router-dom";
import { LayoutDashboard, MessagesSquare, CreditCard, FileText, LifeBuoy, LogOut, Plus, Clock, Check, X, Download, HelpCircle, KeyRound } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AskSheet from "@/components/AskSheet";
import PaymentReturn from "@/pages/PaymentReturn";

const Sidebar = ({ credits, onAsk }) => {
  const items = [
    { to: "/portal", label: "Dashboard", ic: LayoutDashboard, end: true },
    { to: "/portal/questions", label: "My questions", ic: MessagesSquare },
    { to: "/portal/payments", label: "Payments", ic: CreditCard },
    { to: "/portal/invoices", label: "Invoices", ic: FileText },
    { to: "/portal/support", label: "Support", ic: LifeBuoy },
  ];
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <aside className="at-sidebar" data-testid="client-sidebar">
      <div className="at-side-logo">Ask<b>Tax</b></div>
      <button className="at-btn amber block mb-6" onClick={onAsk} data-testid="sidebar-ask-btn">
        <Plus size={16} /> Ask a question
      </button>
      {credits > 0 && (
        <div className="p-3 mb-4 border-2 rounded" style={{ borderColor: "var(--amber)", background: "rgba(232,168,56,0.15)" }} data-testid="credits-badge">
          <div className="text-xs uppercase font-display font-bold tracking-widest" style={{ color: "var(--amber)" }}>Prepaid</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: "var(--cream-2)" }}>{credits}</div>
          <div className="text-xs" style={{ color: "#a3b3c3" }}>question{credits > 1 ? "s" : ""} left</div>
        </div>
      )}
      {items.map(({ to, label, ic: Ic, end }) => (
        <NavLink key={to} to={to} end={end} className={({ isActive }) => `at-side-item ${isActive ? "active" : ""}`} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>
          <Ic size={16} /> {label}
        </NavLink>
      ))}
      <div className="mt-8 pt-4 border-t border-gray-700">
        <NavLink to="/change-password" className="at-side-item w-full" data-testid="nav-change-password">
          <KeyRound size={16} /> Change password
        </NavLink>
        <button onClick={() => { logout(); nav("/"); }} className="at-side-item w-full" data-testid="logout-btn">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
};

const PillState = ({ st }) => st === "answered"
  ? <span className="pill pill-answered" data-testid="pill-answered"><Check size={12} /> Answered</span>
  : <span className="pill pill-progress" data-testid="pill-progress"><Clock size={12} /> In progress</span>;

// --- Pages ---
function Dashboard({ onAsk }) {
  const [d, setD] = useState(null);
  const [qs, setQs] = useState([]);
  useEffect(() => {
    api.get("/my/dashboard").then(r => setD(r.data));
    api.get("/my/questions").then(r => setQs(r.data.slice(0, 3)));
  }, []);
  if (!d) return null;
  return (
    <>
      <div className="at-main-head">
        <div>
          <span className="eyebrow">Your portal</span>
          <h1 className="at-h1">Good day, <em>{d.name}</em></h1>
        </div>
        <button onClick={onAsk} className="at-btn" data-testid="dash-ask-btn"><Plus size={16} /> Ask a question</button>
      </div>
      <div className="at-stats">
        <div className="at-stat"><div className="at-stat-l">Questions asked</div><div className="at-stat-v" data-testid="stat-asked">{d.asked}</div><div className="at-stat-s">Total</div></div>
        <div className="at-stat"><div className="at-stat-l">Answered</div><div className="at-stat-v" data-testid="stat-answered">{d.answered}</div><div className="at-stat-s">Ready to read</div></div>
        <div className="at-stat"><div className="at-stat-l">In progress</div><div className="at-stat-v" data-testid="stat-inprogress">{d.in_progress}</div><div className="at-stat-s">Being answered</div></div>
        <div className="at-stat"><div className="at-stat-l">Total spent</div><div className="at-stat-v"><span className="cur">$</span>{d.total_spent}</div><div className="at-stat-s">All time</div></div>
      </div>
      <div className="at-card">
        <div className="at-card-head"><div className="at-card-title">Recent questions</div></div>
        {qs.length === 0 && <div className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No questions yet — ask your first.</div>}
        {qs.map(q => (
          <div key={q.id} className="q-item">
            <div className="q-num"><HelpCircle size={20} /></div>
            <div>
              <div className="q-cat">{q.category}</div>
              <div className="q-text">{q.question_text}</div>
              <div className="q-meta">Ref {q.ref} · {new Date(q.created_at).toLocaleDateString("en-NZ")}</div>
            </div>
            <div className="q-right"><PillState st={q.client_stage} /></div>
          </div>
        ))}
      </div>
    </>
  );
}

function Questions({ onView }) {
  const [qs, setQs] = useState([]);
  useEffect(() => { api.get("/my/questions").then(r => setQs(r.data)); }, []);
  return (
    <>
      <div className="at-main-head">
        <div><span className="eyebrow">Your Q&A</span><h1 className="at-h1">My <em>questions</em></h1></div>
      </div>
      <div className="at-card">
        {qs.length === 0 && <div className="p-12 text-center font-serif italic" style={{ color: "var(--muted)" }}>Nothing here yet.</div>}
        {qs.map(q => (
          <div key={q.id} className="q-item" data-testid={`question-${q.ref}`}>
            <div className="q-num"><HelpCircle size={20} /></div>
            <div>
              <div className="q-cat">{q.category}</div>
              <div className="q-text">{q.question_text}</div>
              <div className="q-meta">Ref {q.ref} · Asked {new Date(q.created_at).toLocaleDateString("en-NZ")}{q.answered_at ? ` · Answered ${new Date(q.answered_at).toLocaleDateString("en-NZ")}` : ""}</div>
            </div>
            <div className="q-right">
              <PillState st={q.client_stage} />
              {q.client_stage === "answered" && (
                <button className="at-btn ghost xs" onClick={() => onView(q)} data-testid={`read-${q.ref}`}>Read answer</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Payments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/my/payments").then(r => setRows(r.data)); }, []);
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">History</span><h1 className="at-h1">Payments</h1></div></div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Item</th><th>Amount</th><th>Method</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td>{r.item}</td>
                <td className="font-bold">${r.amount.toFixed(2)}</td>
                <td style={{ color: "var(--muted)" }}>{r.method}</td>
                <td style={{ color: "var(--muted)" }}>{new Date(r.created_at).toLocaleDateString("en-NZ")}</td>
                <td><span className="pill pill-paid">Paid</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Invoices() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/my/invoices").then(r => setRows(r.data)); }, []);
  const dl = async (no) => {
    const r = await api.get(`/invoices/${no}/pdf`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">GST invoices</span><h1 className="at-h1">Invoices</h1></div></div>
      <div className="at-card">
        <table className="at-tbl">
          <thead><tr><th>Invoice #</th><th>Amount (incl. GST)</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td className="font-bold">{r.invoice_no}</td>
                <td className="font-bold">${r.amount.toFixed(2)} <span className="font-normal" style={{ color: "var(--muted-2)" }}>(incl. ${r.gst_amount.toFixed(2)} GST)</span></td>
                <td style={{ color: "var(--muted)" }}>{new Date(r.created_at).toLocaleDateString("en-NZ")}</td>
                <td><span className="pill pill-paid">Paid</span></td>
                <td className="text-right"><button className="at-btn ghost xs" onClick={() => dl(r.invoice_no)} data-testid={`inv-pdf-${r.invoice_no}`}><Download size={12} /> PDF</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center font-serif italic" style={{ color: "var(--muted)" }}>No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Support({ onAsk }) {
  const [topic, setTopic] = useState("A question I've asked");
  const [linked, setLinked] = useState("");
  const [msg, setMsg] = useState("");
  const [mine, setMine] = useState([]);
  const [qs, setQs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/support/my").then(r => setMine(r.data));
    api.get("/my/questions").then(r => setQs(r.data));
  }, []);
  const send = async () => {
    if (msg.trim().length < 5) { toast.error("Message is too short"); return; }
    setBusy(true);
    try {
      await api.post("/support/tickets", { topic, linked_query_ref: linked || null, message: msg });
      toast.success("Message sent — we'll reply within one business day.");
      setMsg(""); setLinked("");
      const r = await api.get("/support/my"); setMine(r.data);
    } catch { toast.error("Could not send"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="at-main-head"><div><span className="eyebrow">Help</span><h1 className="at-h1">Get in <em>touch</em></h1></div></div>
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6">
        <div className="at-card">
          <div className="at-card-head"><div className="at-card-title">Send us a message</div></div>
          <div className="p-6">
            <div><label className="at-label">What's this about?</label>
              <select className="at-select" value={topic} onChange={e => setTopic(e.target.value)} data-testid="support-topic">
                <option>A question I've asked</option>
                <option>Billing or invoices</option>
                <option>My account</option>
                <option>Something else</option>
              </select>
            </div>
            {topic === "A question I've asked" && (
              <div className="mt-4"><label className="at-label">Which question? (optional)</label>
                <select className="at-select" value={linked} onChange={e => setLinked(e.target.value)} data-testid="support-linked">
                  <option value="">— none —</option>
                  {qs.map(q => <option key={q.ref} value={q.ref}>{q.ref} — {q.question_text.slice(0, 60)}…</option>)}
                </select>
              </div>
            )}
            <div className="mt-4"><label className="at-label">Message</label>
              <textarea className="at-textarea" maxLength={1500} placeholder="Tell us how we can help. For a new tax question, use 'Ask a question' instead." value={msg} onChange={e => setMsg(e.target.value)} data-testid="support-msg" />
              <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>We usually reply within one business day · {msg.length}/1500</div>
            </div>
            <button onClick={send} disabled={busy} className="at-btn block mt-4" data-testid="support-send">{busy ? "Sending…" : "Send message"}</button>
          </div>
        </div>
        <div className="space-y-6">
          <div className="at-card p-6">
            <div className="text-xs font-display font-bold uppercase tracking-widest" style={{ color: "var(--muted)" }}>Email us</div>
            <a className="block mt-2 font-display font-black text-lg" style={{ color: "var(--kiwi)" }} href="mailto:help@asktax.co.nz">help@asktax.co.nz</a>
            <div className="mt-3 text-sm font-serif italic" style={{ color: "var(--muted)" }}>A real person replies — usually within one business day.</div>
          </div>
          <div className="at-card p-6" style={{ background: "var(--cream-3)" }}>
            <div className="font-display font-bold uppercase tracking-wide text-sm">Have a new tax question?</div>
            <div className="mt-2 font-serif" style={{ color: "var(--ink-2)" }}>Support is for help with your account, billing, or an answer you've received. For a new tax question, use <b>Ask a question</b>.</div>
            <button onClick={onAsk} className="at-btn ghost mt-4" data-testid="support-ask-btn">Ask a question</button>
          </div>
        </div>
      </div>
      {mine.length > 0 && (
        <div className="at-card mt-6">
          <div className="at-card-head"><div className="at-card-title">Your recent messages</div></div>
          {mine.map(m => (
            <div key={m.id} className="p-5 border-b" style={{ borderColor: "var(--hair)" }} data-testid={`support-msg-${m.ref}`}>
              <div className="flex justify-between items-center">
                <div className="font-display font-bold uppercase tracking-wide text-sm">{m.topic} · <span style={{ color: "var(--muted)" }}>{m.ref}</span></div>
                <span className={`pill ${m.status === "replied" ? "pill-answered" : m.status === "resolved" ? "pill-paid" : "pill-progress"}`}>{m.status}</span>
              </div>
              <div className="mt-2 font-serif italic">{m.message}</div>
              {m.admin_reply && (
                <div className="mt-3 p-3 border-l-4" style={{ borderColor: "var(--kiwi)", background: "var(--cream-3)" }}>
                  <div className="text-xs font-display font-bold uppercase tracking-wide" style={{ color: "var(--kiwi)" }}>Reply from AskTax</div>
                  <div className="mt-1 font-serif">{m.admin_reply}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function AnswerSheet({ q, onClose }) {
  return (
    <div className="overlay" onClick={onClose} data-testid="answer-sheet">
      <div className="sheet" onClick={e => e.stopPropagation()}>
        <div className="sheet-head">
          <div className="sheet-title">Your <em>answer</em></div>
          <button onClick={onClose}><X size={22} /></button>
        </div>
        <div className="sheet-body">
          <div className="at-tag">{q.category}</div>
          <div className="mt-3 font-serif italic text-lg" style={{ color: "var(--ink-2)" }}>{q.question_text}</div>
          <div className="mt-6 pt-6 border-t-2" style={{ borderColor: "var(--ink)" }}>
            <div className="eyebrow">Answer</div>
            <div className="mt-3 font-serif text-base leading-relaxed markdown-answer" data-testid="answer-body"><ReactMarkdown>{q.final_answer || ""}</ReactMarkdown></div>
            <div className="mt-6 pt-4 border-t border-dashed" style={{ borderColor: "var(--hair)" }} data-testid="answer-disclaimer">
              <div className="text-[10px] font-display font-bold uppercase tracking-widest mb-2" style={{ color: "var(--muted-2)" }}>Please note</div>
              <p className="text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
                This answer is general guidance based only on the information you provided, and is not a full review, audit, or formal tax opinion. It doesn't account for anything not mentioned in your question. If your circumstances are more complex or change, or you need advice you can formally rely on, please seek a full engagement. AskTax and Jiten Joshi &amp; Associates Limited accept no liability for decisions made on the basis of incomplete or inaccurate information provided.
              </p>
            </div>
          </div>
          {q.answer_files && q.answer_files.length > 0 && (
            <div className="mt-6 pt-6 border-t border-dashed" style={{ borderColor: "var(--hair)" }}>
              <div className="eyebrow">Attachments</div>
              <div className="mt-3 space-y-2">
                {q.answer_files.map(f => (
                  <a key={f.id} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}?token=${localStorage.getItem("asktax_token")}`} onClick={async (e) => {
                    e.preventDefault();
                    const r = await api.get(f.url.replace("/api", ""), { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
                    URL.revokeObjectURL(url);
                  }} className="at-card p-3 flex justify-between items-center hover:opacity-90 cursor-pointer" data-testid={`attach-${f.id}`}>
                    <div className="flex items-center gap-3"><FileText size={18} /><span>{f.name}</span></div>
                    <Download size={16} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientPortal() {
  const [ask, setAsk] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [credits, setCredits] = useState(0);
  const refresh = () => api.get("/credits").then(r => setCredits(r.data.credits_remaining));
  useEffect(() => { refresh(); }, []);

  return (
    <div className="at-shell">
      <Sidebar credits={credits} onAsk={() => setAsk(true)} />
      <main className="at-main">
        <Routes>
          <Route index element={<Dashboard onAsk={() => setAsk(true)} />} />
          <Route path="questions" element={<Questions onView={setViewing} />} />
          <Route path="payments" element={<Payments />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="support" element={<Support onAsk={() => setAsk(true)} />} />
          <Route path="return" element={<PaymentReturn />} />
        </Routes>
      </main>
      {ask && <AskSheet credits={credits} onClose={() => { setAsk(false); refresh(); }} onSubmitted={refresh} />}
      {viewing && <AnswerSheet q={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
